import {dashboardApi} from "../api";
import type {
  DashboardKpi,
  DashboardKpiKey,
  DashboardOverview,
  DashboardOverviewFilters,
} from "../types";

class DashboardService {
  async getOverview(filters: DashboardOverviewFilters): Promise<DashboardOverview> {
    const {data} = await dashboardApi.getOverview(filters);
    const normalized = this.normalizeOverviewResponse(data);

    if (!normalized) {
      throw new Error("Invalid dashboard overview payload");
    }

    return normalized;
  }

  /**
   * Index KPIs by key for O(1) lookup in render code; preserves the
   * raw shape so consumers narrow on `key`.
   */
  indexKpis(kpis: DashboardKpi[]): Partial<Record<DashboardKpiKey, DashboardKpi>> {
    return Object.fromEntries(kpis.map((k) => [k.key, k])) as Partial<
      Record<DashboardKpiKey, DashboardKpi>
    >;
  }

  private normalizeOverviewResponse(payload: unknown): DashboardOverview | null {
    if (this.hasOverviewShape(payload)) return this.normalizeOverview(payload);

    if (
      this.isRecord(payload) &&
      "data" in payload &&
      this.hasOverviewShape(payload.data)
    ) {
      return this.normalizeOverview(payload.data);
    }

    return null;
  }

  private normalizeOverview(value: DashboardOverview): DashboardOverview {
    return {
      ...value,
      management: this.normalizeManagement(value.management),
    };
  }

  private normalizeManagement(value: unknown): DashboardOverview["management"] {
    const management = this.asRecord(value);
    const officers = this.asRecord(management.officers);
    const shifts = this.asRecord(management.shifts);
    const properties = this.asRecord(management.properties);

    const officersMissed = this.asRecord(officers.missed_shift_in_period);
    const shiftsMissed = this.asRecord(shifts.missed_in_period);

    const officerItems = this.asArray(officersMissed.items).map((item) => {
      const row = this.asRecord(item);
      return {
        user_id: this.toNumber(row.user_id ?? row.id),
        name:
          this.toText(
            row.name ??
              row.user_name ??
              row.officer_name ??
              this.asRecord(row.user).name ??
              this.asRecord(row.created_by).name,
          ) || "Unassigned",
        missed_count: this.toCount(row.missed_count ?? row.count),
      };
    });

    const shiftItems = this.asArray(shiftsMissed.items).map((item, idx) => {
      const row = this.asRecord(item);
      const property = this.asRecord(row.property);
      const user = this.asRecord(row.user);
      const shiftId = this.toNumber(row.shift_id ?? row.id) || idx + 1;
      return {
        shift_id: shiftId,
        property_id: this.toOptionalNumber(row.property_id ?? property.id),
        property_name:
          this.toText(row.property_name ?? property.name ?? row.context_property_name) ||
          undefined,
        user_id: this.toOptionalNumber(row.user_id ?? user.id),
        user_name:
          this.toText(
            row.user_name ??
              row.name ??
              row.officer_name ??
              user.name ??
              this.asRecord(row.created_by).name,
          ) || undefined,
        started_at: this.toOptionalText(row.started_at),
        ended_at: this.toOptionalText(row.ended_at),
      };
    });

    const byTierRaw = this.asRecord(properties.by_tier);
    const by_tier = Object.fromEntries(
      Object.entries(byTierRaw).map(([tier, rawValue]) => {
        const row = this.asRecord(rawValue);
        return [
          tier,
          {
            active: this.toCount(row.active ?? row.total ?? rawValue),
            expiring_soon: this.toCount(row.expiring_soon),
          },
        ];
      }),
    );

    const expiring_soon_items = this.asArray(properties.expiring_soon_items).map((item) => {
      const row = this.asRecord(item);
      return {
        id: this.toNumber(row.id),
        name: this.toText(row.name) || "Unnamed property",
        service_tier: (this.toText(row.service_tier) || "paid") as
          | "paid"
          | "free_limited"
          | "free_inspection",
        expires_at: this.toOptionalText(row.expires_at),
        days_until_expiration: this.toOptionalNumber(row.days_until_expiration),
      };
    });

    return {
      officers: {
        active_now: this.toCount(officers.active_now),
        worked_in_period: this.toCount(officers.worked_in_period),
        missed_shift_in_period: {
          count: this.toCount(officersMissed.count ?? officerItems.length),
          items: officerItems,
        },
      },
      properties: {
        by_tier,
        expiring_soon_items,
      },
      shifts: {
        total_in_period: this.toCount(shifts.total_in_period),
        worked_in_period: this.toCount(shifts.worked_in_period),
        missed_in_period: {
          count: this.toCount(shiftsMissed.count ?? shiftItems.length),
          items: shiftItems,
        },
      },
    };
  }

  private hasOverviewShape(value: unknown): value is DashboardOverview {
    if (!this.isRecord(value)) return false;

    return (
      this.isRecord(value.meta) &&
      this.isRecord(value.period) &&
      Array.isArray(value.kpis) &&
      this.isRecord(value.time_series) &&
      this.isRecord(value.breakdowns) &&
      this.isRecord(value.emails) &&
      this.isRecord(value.management)
    );
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return value != null && typeof value === "object";
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return this.isRecord(value) ? value : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private toNumber(value: unknown): number {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
  }

  private toCount(value: unknown): number {
    if (this.isRecord(value)) {
      return this.toNumber(value.count);
    }
    return this.toNumber(value);
  }

  private toText(value: unknown): string {
    return typeof value === "string" ? value : "";
  }

  private toOptionalText(value: unknown): string | undefined {
    const text = this.toText(value);
    return text || undefined;
  }

  private toOptionalNumber(value: unknown): number | undefined {
    if (value === undefined || value === null || value === "") return undefined;
    const num = this.toNumber(value);
    return Number.isFinite(num) ? num : undefined;
  }
}

export const dashboardService = new DashboardService();
